import React, { useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { JumpIcon, NoteIcon } from '@modernloop/shared/icons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Theme,
  Typography,
} from '@mui/material';

import { ApplicationNote_ApplicationFragment } from 'src/generated/mloop-graphql';

import Editor from 'src/components/Editor';
import { FCWithFragments } from 'src/components/types';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import ApplicationNoteEditor from './ApplicationNoteEditor';

type Fragments = {
  application: ApplicationNote_ApplicationFragment;
};

const ApplicationNote: FCWithFragments<Fragments> = ({ application }) => {
  const { t } = useTranslation();
  const [showEditor, setShowEditor] = useState(false);

  return (
    <Accordion variant="outlined" disableGutters defaultExpanded>
      <AccordionSummary>
        <Stack>
          <Typography variant="subtitle2">{t('hiring-dashboard-candidates:candidates.internalNote.title')}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack rowGap={2}>
          {application.internalNote ? (
            <Stack rowGap={2}>
              <Editor
                readOnly
                onChange={() => {}}
                value={application.internalNote || ''}
                modules={{ toolbar: false }}
                sx={{
                  border: 'none',
                  boxShadow: 'none',
                  '& .ql-editor': {
                    padding: '0',
                  },
                }}
              />
              <Stack direction="row" justifyContent="flex-end">
                <Button onClick={() => setShowEditor(true)} size="small">
                  {t('hiring-dashboard-candidates:candidates.internalNote.edit')}
                </Button>
              </Stack>
              <Divider />
              <ListItemButton
                sx={{
                  marginX: -1.5,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  window.open(HelpCenterUrl.COMMUNICATION_TOKENS, '_blank');
                }}
              >
                <ListItemIcon
                  sx={{
                    color: (theme: Theme) => theme.palette.primary.main,
                  }}
                >
                  <JumpIcon />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    color: (theme: Theme) => theme.palette.primary.main,
                  }}
                >
                  {t('hiring-dashboard-candidates:candidates.internalNote.learnMore')}
                </ListItemText>
              </ListItemButton>
            </Stack>
          ) : (
            <Stack rowGap={1.5}>
              <ListItemButton
                sx={{
                  marginX: -1.5,
                  cursor: 'pointer',
                }}
                onClick={() => setShowEditor(true)}
              >
                <ListItemIcon
                  sx={{
                    color: (theme: Theme) => theme.palette.primary.main,
                  }}
                >
                  <NoteIcon />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    color: (theme: Theme) => theme.palette.primary.main,
                  }}
                >
                  {t('hiring-dashboard-candidates:candidates.internalNote.addInternalNote')}
                </ListItemText>
              </ListItemButton>
              <Divider />
              <ListItemButton
                sx={{
                  marginX: -1.5,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  window.open(HelpCenterUrl.COMMUNICATION_TOKENS, '_blank');
                }}
              >
                <ListItemIcon
                  sx={{
                    color: (theme: Theme) => theme.palette.primary.main,
                  }}
                >
                  <JumpIcon />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    color: (theme: Theme) => theme.palette.primary.main,
                  }}
                >
                  {t('hiring-dashboard-candidates:candidates.internalNote.learnMore')}
                </ListItemText>
              </ListItemButton>
            </Stack>
          )}
          {showEditor && <ApplicationNoteEditor onClose={() => setShowEditor(false)} application={application} />}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ApplicationNoteUpdateMutation = gql`
  mutation applicationNoteUpdateMutation($input: ApplicationUpdateInput!) {
    applicationUpdate(input: $input) {
      id
      internalNote
    }
  }
`;

ApplicationNote.fragments = {
  application: gql`
    ${ApplicationNoteEditor.fragments.application}
    fragment ApplicationNote_application on Application {
      id
      internalNote
      ...ApplicationNoteEditor_application
    }
  `,
};

export default ApplicationNote;
